import React from 'react';
import {Box, Stack} from '@mui/material';
import {getMarkerTheme} from './markerTheme';
import {getTaskTitle} from '../../../../utils/taskHelpers';
import {useTaskDoc} from './hooks/useTaskDoc';
import {useReplaceTaskText} from './hooks/useReplaceTaskText';
import {useLinkEntityFromTask} from './hooks/useLinkEntityFromTask';
import TaskMarkerHeader from './components/TaskMarkerHeader';
import TaskMarkerContent from './components/TaskMarkerContent';

export default function TaskMarker({marker, isActive, onClick, reportId, view, onDelete}) {
    const {data: taskData, loading, notFound} = useTaskDoc(reportId, marker?.uid);

    // getMarkerTheme returns stable config objects for known types; memo isn't needed.
    const cfg = getMarkerTheme('task');

    // Use data from Firestore, fallback to marker data if not loaded yet
    const task = taskData || marker?.data || marker;
    const title = getTaskTitle(task);

    // Hook for handling text replacement
    const {handleReplaceText, isReplacing} = useReplaceTaskText({
        view,
        task,
        reportId,
        marker
    });

    // Hook for linking entities (entity_recognition tasks)
    const {handleLinkEntity, canLinkEntity} = useLinkEntityFromTask({view, task});

    return (
        <Box
            className="pm-margin-marker pm-margin-marker--expanded"
            sx={{
                width: '100%',
                minHeight: 40,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: isActive ? `1px solid ${cfg.border}` : '1px solid transparent',
                boxShadow: isActive ? '0 6px 18px rgba(0,0,0,0.18)' : '0 0px 0px rgba(0,0,0,0)',
                userSelect: 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                zIndex: isActive ? 999 : 'auto',
                ...(!isActive && {
                    '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.04)',
                    },
                }),
            }}
        >
            <TaskMarkerHeader
                task={task}
                title={title}
                isActive={isActive}
                onClick={onClick}
                onDelete={onDelete}
                marker={marker}
                cfg={cfg}
            />

            {isActive && (
                <Stack spacing={1} sx={{width: '100%', p: 2, pt: 1, overflowY: 'auto'}}>
                    <TaskMarkerContent
                        task={task}
                        loading={loading}
                        notFound={notFound}
                        onReplaceText={handleReplaceText}
                        isReplacing={isReplacing}
                        onLinkEntity={handleLinkEntity}
                        canLinkEntity={canLinkEntity}
                        viewAvailable={!!view}
                    />
                </Stack>
            )}
        </Box>
    );
}
